import getExpensePerUnit from 'services/products/calculations/getExpensePerUnit';
import getExpenseTotal from 'services/products/calculations/getExpenseTotal';
import getInstallationsTotalInstallationPrice from 'services/products/calculations/getInstallationsTotalInstallationPrice';
import getInstallationsUnitInstallationPrice from 'services/products/calculations/getInstallationsUnitInstallationPrice';
import IProductInstallationCategory from 'api/interfaces/products/IProductInstallationCategory';
import numericStringToFloat from 'services/numbers/numericStringToFloat';
import {
  getContractValues,
  sectionOf,
} from 'services/offers/installationSections';
import { getWorkDays } from 'services/offers/workDays';
import { INSTALLATION } from 'components/offers/services/installationTypes';

import getPriceSummary from '../getPriceSummary';
import IVersion from '../IVersion';

/**
 * The summary row of the installation contract, or of the section of subcontracting or own
 * production: its rows (of the installation categories in the section), their sums, and the
 * hours from the average hourly earnings of the summary row.
 */
const getVersionInstallationContract = (
  version: IVersion,
  quantity: number,
  installationCategories: IProductInstallationCategory[],
  // null for the installation contract
  section: null | string = null
): {
  contributionMarginEurForH: number;
  // The selling price and margin of the rows of the summary row
  installationsContributionMargin: number;
  installationsExpensePerUnit: number;
  installationsExpenseTotal: number;
  installationsInstallationPriceSum: number;
  installationsUnitInstallationPrice: number;
  installationsSellingPriceTotal: number;
  totalHours: number;
  unitForH: number;
} => {
  const installationsWithoutOtherExpenses = version.installations.filter(
    (installation) =>
      sectionOf(installation) === section &&
      installationCategories.find(
        (installationCategory: IProductInstallationCategory) =>
          installationCategory.type === INSTALLATION &&
          installationCategory.category === installation.category
      )
  );

  // The sums of the rows of the summary row
  const rowsSummary = getPriceSummary(
    installationsWithoutOtherExpenses,
    [],
    quantity
  );

  // The margin per hour of the installation contract is that of the position without the
  // sections, as before them; that of a section is of its own rows
  const priceSummary =
    section === null
      ? getPriceSummary(
          version.installations.filter(
            (installation) => sectionOf(installation) === null
          ),
          version.subproducts,
          quantity
        )
      : rowsSummary;

  const installationsExpenseTotal = getExpenseTotal(
    installationsWithoutOtherExpenses,
    [],
    quantity
  );

  // The installation contract for one unit including the social security costs
  const installationsExpensePerUnit = getExpensePerUnit(
    installationsWithoutOtherExpenses,
    []
  );

  const installationsInstallationPriceSum =
    getInstallationsTotalInstallationPrice(
      installationsWithoutOtherExpenses,
      quantity
    );

  const installationsUnitInstallationPrice =
    getInstallationsUnitInstallationPrice(installationsWithoutOtherExpenses);

  const numericAHE = numericStringToFloat(
    getContractValues(version, section).averageHourlyEarnings
  );

  const totalHours =
    numericAHE === 0 ? 0 : installationsInstallationPriceSum / numericAHE;

  const unitForH = totalHours === 0 ? 0 : quantity / totalHours;

  const contributionMarginEurForH =
    totalHours === 0 ? 0 : priceSummary.contributionMargin / totalHours;

  return {
    contributionMarginEurForH,
    installationsExpensePerUnit,
    installationsExpenseTotal,
    installationsInstallationPriceSum,
    installationsContributionMargin: rowsSummary.contributionMargin,
    installationsSellingPriceTotal: rowsSummary.sellingPrice,
    installationsUnitInstallationPrice,
    totalHours,
    unitForH,
  };
};

/** The work days of the summary row of the version for its workers */
export const getVersionWorkDays = (
  version: IVersion,
  quantity: number,
  installationCategories: IProductInstallationCategory[],
  section: null | string = null
): number =>
  getWorkDays(
    getVersionInstallationContract(
      version,
      quantity,
      installationCategories,
      section
    ).totalHours,
    numericStringToFloat(getContractValues(version, section).installerCount)
  );

/**
 * The worker days of the summary row: its work days for each of its workers, e.g. 10 days
 * of 3 installers make 30 per diems. As many as its hours make 8 h days.
 */
export const getVersionWorkerDays = (
  version: IVersion,
  quantity: number,
  installationCategories: IProductInstallationCategory[],
  section: null | string = null
): number =>
  getVersionWorkDays(version, quantity, installationCategories, section) *
  numericStringToFloat(getContractValues(version, section).installerCount);

/**
 * The total of the summary rows of the installation contract and the sections in use: the
 * sums of their expenses, contracts, selling prices, margins and hours, the average hourly
 * earnings of all the hours (the contracts divided by the hours), and the sums of the
 * workers and the work days of the summary rows that have hours.
 */
export const getContractsTotal = (
  version: IVersion,
  quantity: number,
  installationCategories: IProductInstallationCategory[],
  sections: string[]
): {
  averageHourlyEarnings: number;
  contract: number;
  expense: number;
  hours: number;
  installers: number;
  margin: number;
  sellingPrice: number;
  workDays: number;
} => {
  const rows = [null, ...sections].map((section) => ({
    contract: getVersionInstallationContract(
      version,
      quantity,
      installationCategories,
      section
    ),
    installers: numericStringToFloat(
      getContractValues(version, section).installerCount
    ),
    workDays: getVersionWorkDays(
      version,
      quantity,
      installationCategories,
      section
    ),
  }));
  const sum = (value: (row: (typeof rows)[number]) => number) =>
    rows.reduce((total, row) => total + value(row), 0);

  // The workers and work days of the summary rows that have hours
  const withHours = rows.filter((row) => row.contract.totalHours > 0);
  const sumWithHours = (value: (row: (typeof rows)[number]) => number) =>
    withHours.reduce((total, row) => total + value(row), 0);

  const contract = sum((row) => row.contract.installationsInstallationPriceSum);
  const hours = sum((row) => row.contract.totalHours);

  return {
    averageHourlyEarnings: hours > 0 ? contract / hours : 0,
    contract,
    expense: sum((row) => row.contract.installationsExpenseTotal),
    hours,
    installers: sumWithHours((row) => row.installers),
    margin: sum((row) => row.contract.installationsContributionMargin),
    sellingPrice: sum((row) => row.contract.installationsSellingPriceTotal),
    workDays: sumWithHours((row) => row.workDays),
  };
};

export default getVersionInstallationContract;
